import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Home, Search, MapPin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useEffect } from 'react';

export default function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  useEffect(() => { document.title = "Page not found | OneCheckUp"; }, []);

  const getHomepage = () => {
    if (!currentUser) return '/login';

    switch (currentUser.role) {
      case 'staff':
        return '/frontdesk';
      case 'doctor':
        return '/';
      case 'admin':
        return '/settings';
      default:
        return '/';
    }
  };

  const handleGoHome = () => {
    navigate(getHomepage());
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoToPatients = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Staff goes to receptionist patients
    if (currentUser.role === 'staff') {
      navigate('/frontdesk/patients');
    } else {
      navigate('/patients');
    }
  };

  // Optional: Auto-redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000); // Redirect after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [currentUser, navigate]);

  return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-12 h-12 text-primary" />
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                404 - Page Not Found
              </h1>
              <p className="text-muted-foreground mb-4">
                Sorry, the page you're looking for doesn't exist or has been moved.
              </p>

              {!currentUser && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
                    Redirecting to login in 3 seconds...
                  </p>
              )}

              <div className="bg-muted/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-muted-foreground">
                  <strong>Requested URL:</strong>
                  <code className="ml-2 bg-background px-2 py-1 rounded text-xs">
                    {location.pathname}
                  </code>
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={handleGoBack} variant="outline" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </Button>

                {currentUser && (
                    <Button onClick={handleGoToPatients} variant="outline" className="gap-2">
                      <Search className="w-4 h-4" />
                      View Patients
                    </Button>
                )}

                <Button onClick={handleGoHome} className="gap-2">
                  <Home className="w-4 h-4" />
                  {currentUser ? 'Go to Dashboard' : 'Go to Login'}
                </Button>
              </div>

              {currentUser && (
                  <div className="mt-8 text-sm text-muted-foreground">
                    <h3 className="font-medium mb-2">Popular Pages:</h3>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {(currentUser.role === 'doctor' || currentUser.role === 'staff') && (
                          <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(currentUser.role === 'staff' ? '/frontdesk/patients' : '/patients')}
                              className="text-xs"
                          >
                            Patient Management
                          </Button>
                      )}
                      {(currentUser.role === 'doctor' || currentUser.role === 'staff') && (
                          <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(currentUser.role === 'staff' ? '/frontdesk/appointments' : '/appointments')}
                              className="text-xs"
                          >
                            Appointments
                          </Button>
                      )}
                      {currentUser.role === 'doctor' && (
                          <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate('/prescriptions')}
                              className="text-xs"
                          >
                            Prescriptions
                          </Button>
                      )}
                    </div>
                  </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
  );
}